import { useSetState } from '@domain-group/react-hooks'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { baseURL } from '@functions/api'
import callApi from '@functions/callApi'

import useCampaignComparables from '@hooks/useCampaignComparables'

export type PropertySuggestion = {
  address: string,
  agency_id: number,
  country_code: string,
  data_provider: string,
  hidden: boolean,
  id: number,
  legal_description: string,
  post_code: string,
  state_name: string,
  street_name: string,
  street_number: string,
  suburb_name: string,
  type: string,
  unit_number: string,
}

const defaultState = {
  agencyResults: [] as PropertySuggestion[],
  marketResults: [] as PropertySuggestion[],
  searchComplete: false,
  searchString: '',
  searchedString: '',
  searching: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const sortResults = (searchResult: PropertySuggestion[]) => searchResult.reduce(
  (obj, result) => {
    if (result.data_provider === 'realhub'){
      // @ts-expect-error TS2345
      obj.agencyResults.push(result)
    } else {
      // @ts-expect-error TS2345
      obj.marketResults.push(result)
    }

    return obj
  },
  { agencyResults: [], marketResults: [] },
)

type CombinedSearchParams = {
  setState: SetState,
  string: string,
}

const combinedSearch = async (params: CombinedSearchParams) => {
  const { setState, string } = params
  if (string === '') return

  setState({ searching: true })

  const endpoint = `${baseURL}/campaign_comparables/combined_search`
  const result = await callApi<PropertySuggestion[]>(endpoint, { string })

  if (result.success){
    const sortedResult = sortArrayBy(result.data, 'asc', 'address')
    const { agencyResults, marketResults } = sortResults(sortedResult)

    setState({
      agencyResults, marketResults, searchComplete: true, searchedString: string, searching: false,
    })
  }

  return result
}

type UseSearchCampaignComparablesParams = {
  campaignId: number,
}

function useSearchCampaignComparables(params: UseSearchCampaignComparablesParams) {
  const { campaignId } = params

  const [state, setState] = useSetState(defaultState)
  const {
    agencyResults, marketResults, searchComplete, searchString, searchedString, searching,
  } = state

  const campaignComparablesPayload = useCampaignComparables({ filters: { campaign_id: campaignId } })
  const { filteredCampaignComparables } = campaignComparablesPayload

  const hasAgencyResults = !!agencyResults.length
  const hasMarketResults = !!marketResults.length

  return {
    agencyResults,
    callbacks: {
      combinedSearch: (string: string) => combinedSearch({ string, setState }),
      setModalState: (values: Partial<typeof defaultState>) => setState(values),
    },
    filteredCampaignComparables,
    hasAgencyResults,
    hasMarketResults,
    marketResults,
    searchComplete,
    searchString,
    searchedString,
    searching,
  }
}

export default useSearchCampaignComparables
